import { useState, useEffect } from "react";
import ResellerSidebar from "@/pages/reseller/components/ResellerSidebar";
import { mockLeaderboard, mockLeaderboardStats, tierColors } from "@/mocks/leaderboard";

const platformIcons: Record<string, string> = {
  TikTok: "ri-tiktok-line",
  YouTube: "ri-youtube-line",
  Instagram: "ri-instagram-line",
  Douyin: "ri-tiktok-line",
  WeChat: "ri-wechat-line",
  Twitter: "ri-twitter-x-line",
};

const ResellerLeaderboardPage = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState("All");
  const [selectedPeriod, setSelectedPeriod] = useState("This Month");

  useEffect(() => {
    document.title = "Reseller Leaderboard – Top Earners This Month | Crown Prime Mall";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", "See the top-earning resellers on Crown Prime Mall this month. TikTok, Douyin, Instagram, YouTube and WeChat influencers earning commissions by promoting USA and Canada factory products.");
    }

    const siteUrl = import.meta.env.VITE_SITE_URL || "https://example.com";
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "schema-leaderboard";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Reseller Leaderboard – Crown Prime Mall",
      "description": "See the top-earning resellers on Crown Prime Mall. TikTok, Douyin, Instagram, YouTube and WeChat influencers earning commissions by promoting USA and Canada factory products.",
      "url": siteUrl + "/reseller/leaderboard",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": siteUrl },
          { "@type": "ListItem", "position": 2, "name": "Reseller Leaderboard", "item": siteUrl + "/reseller/leaderboard" }
        ]
      }
    });
    if (!document.getElementById("schema-leaderboard")) {
      document.head.appendChild(script);
    }
    return () => {
      const el = document.getElementById("schema-leaderboard");
      if (el) el.remove();
    };
  }, []);

  const platforms = ["All", "TikTok", "Douyin", "Instagram", "YouTube", "WeChat"];

  const filtered = mockLeaderboard.filter((r) =>
    selectedPlatform === "All" || r.platform === selectedPlatform
  );

  const top3 = filtered.slice(0, 3);
  const rest = filtered.slice(3);

  const currentUser = mockLeaderboard.find((r) => r.isCurrentUser);

  return (
    <div className="min-h-screen bg-[#f8f7f5] flex" style={{ fontFamily: "'Inter', sans-serif" }}>
      <ResellerSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between sticky top-0 z-20">
          <div className="flex items-center gap-4">
            <button className="lg:hidden w-8 h-8 flex items-center justify-center text-gray-600 cursor-pointer" onClick={() => setSidebarOpen(true)}>
              <i className="ri-menu-line text-xl"></i>
            </button>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Reseller Leaderboard</h1>
              <p className="text-xs text-gray-400">Top earners this month — April 2026</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-xs outline-none focus:border-[#C41E3A] cursor-pointer bg-white"
            >
              {["This Month", "Last Month", "All Time"].map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
        </header>

        <main className="flex-1 p-6 space-y-6">
          {/* Hero Stats Banner */}
          <div className="bg-gradient-to-r from-[#1a0a0a] via-[#2a0808] to-[#C41E3A] rounded-2xl p-6 text-white">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <i className="ri-trophy-line text-[#D4AF37] text-xl"></i>
                  <span className="text-lg font-extrabold">April 2026 Leaderboard</span>
                </div>
                <p className="text-white/60 text-xs">
                  {mockLeaderboardStats.totalResellers.toLocaleString()} active resellers competing this month
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                {[
                  { label: "Total Paid Out", value: "$" + (mockLeaderboardStats.totalCommissionPaidThisMonth / 1000).toFixed(0) + "K", icon: "ri-money-dollar-circle-line" },
                  { label: "Top Earner", value: "$" + mockLeaderboardStats.topEarnerThisMonth.toLocaleString(), icon: "ri-vip-crown-line" },
                  { label: "Avg. Earnings", value: "$" + mockLeaderboardStats.avgCommissionPerReseller.toLocaleString(), icon: "ri-bar-chart-line" },
                  { label: "New Resellers", value: "+" + mockLeaderboardStats.newResellersThisMonth, icon: "ri-user-add-line" },
                ].map((s) => (
                  <div key={s.label} className="bg-white/10 rounded-xl px-4 py-3 text-center min-w-[90px]">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <i className={s.icon + " text-[#D4AF37] text-sm"}></i>
                    </div>
                    <div className="text-base font-extrabold text-white">{s.value}</div>
                    <div className="text-[10px] text-white/50">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Your Rank Highlight */}
            {currentUser && (
              <div className="bg-white/10 rounded-xl p-4 flex items-center gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-[#D4AF37] rounded-full text-[#1a0a0a] font-extrabold text-base flex-shrink-0">
                  #{currentUser.rank}
                </div>
                <img src={currentUser.avatar} alt={currentUser.name} className="w-10 h-10 rounded-full object-cover object-top border-2 border-[#D4AF37] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-white font-bold text-sm">Your Current Rank</div>
                  <div className="text-white/60 text-xs">{currentUser.handle} · {currentUser.platform} · {currentUser.followers} followers</div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-[#D4AF37] font-extrabold text-base">${currentUser.commissionThisMonth.toLocaleString()}</div>
                  <div className="text-white/50 text-[10px]">this month</div>
                </div>
                <div className="bg-green-500/20 text-green-300 text-xs font-bold px-2 py-1 rounded-full whitespace-nowrap">
                  {currentUser.growth} vs last month
                </div>
              </div>
            )}
          </div>

          {/* Platform Filter */}
          <div className="flex gap-2 flex-wrap">
            {platforms.map((p) => (
              <button
                key={p}
                onClick={() => setSelectedPlatform(p)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                  selectedPlatform === p
                    ? "bg-[#C41E3A] text-white border-[#C41E3A]"
                    : "border-gray-200 text-gray-600 hover:border-[#C41E3A] bg-white"
                }`}
              >
                {p !== "All" && <i className={(platformIcons[p] || "ri-share-line") + " text-xs"}></i>}
                {p}
              </button>
            ))}
          </div>

          {/* Top 3 Podium */}
          {top3.length >= 3 && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="text-sm font-bold text-gray-900 mb-6 flex items-center gap-2">
                <i className="ri-trophy-fill text-[#D4AF37]"></i>
                Top 3 This Month
              </h2>
              <div className="flex items-end justify-center gap-4">
                {/* 2nd Place */}
                <div className="flex flex-col items-center gap-3 flex-1 max-w-[180px]">
                  <div className="relative">
                    <img src={top3[1].avatar} alt={top3[1].name} className="w-16 h-16 rounded-full object-cover object-top border-4 border-gray-300" />
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 flex items-center justify-center bg-gray-400 text-white text-xs font-extrabold rounded-full">2</div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-bold text-gray-900">{top3[1].name}</div>
                    <div className="text-[10px] text-gray-400">{top3[1].handle}</div>
                    <div className="flex items-center justify-center gap-1 mt-1">
                      <i className={(platformIcons[top3[1].platform] || "ri-share-line") + " text-xs text-gray-500"}></i>
                      <span className="text-[10px] text-gray-500">{top3[1].platform}</span>
                    </div>
                  </div>
                  <div className="w-full bg-gray-100 rounded-t-xl h-20 flex flex-col items-center justify-center">
                    <div className="text-base font-extrabold text-gray-700">${(top3[1].commissionThisMonth / 1000).toFixed(1)}K</div>
                    <div className="text-[10px] text-gray-400">{top3[1].salesThisMonth} sales</div>
                  </div>
                </div>

                {/* 1st Place */}
                <div className="flex flex-col items-center gap-3 flex-1 max-w-[200px]">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-vip-crown-fill text-[#D4AF37] text-2xl"></i>
                  </div>
                  <div className="relative">
                    <img src={top3[0].avatar} alt={top3[0].name} className="w-20 h-20 rounded-full object-cover object-top border-4 border-[#D4AF37]" />
                    <div className="absolute -bottom-1 -right-1 w-7 h-7 flex items-center justify-center bg-[#D4AF37] text-white text-sm font-extrabold rounded-full">1</div>
                  </div>
                  <div className="text-center">
                    <div className="text-base font-bold text-gray-900">{top3[0].name}</div>
                    <div className="text-[10px] text-gray-400">{top3[0].handle}</div>
                    <div className="flex items-center justify-center gap-1 mt-1">
                      <i className={(platformIcons[top3[0].platform] || "ri-share-line") + " text-xs text-gray-500"}></i>
                      <span className="text-[10px] text-gray-500">{top3[0].platform} · {top3[0].followers}</span>
                    </div>
                  </div>
                  <div className="w-full bg-gradient-to-t from-[#D4AF37]/30 to-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-t-xl h-28 flex flex-col items-center justify-center">
                    <div className="text-xl font-extrabold text-[#B8860B]">${(top3[0].commissionThisMonth / 1000).toFixed(1)}K</div>
                    <div className="text-[10px] text-gray-500">{top3[0].salesThisMonth} sales</div>
                    <div className="text-[10px] text-green-600 font-bold mt-1">{top3[0].growth}</div>
                  </div>
                </div>

                {/* 3rd Place */}
                <div className="flex flex-col items-center gap-3 flex-1 max-w-[180px]">
                  <div className="relative">
                    <img src={top3[2].avatar} alt={top3[2].name} className="w-16 h-16 rounded-full object-cover object-top border-4 border-amber-600" />
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 flex items-center justify-center bg-amber-600 text-white text-xs font-extrabold rounded-full">3</div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-bold text-gray-900">{top3[2].name}</div>
                    <div className="text-[10px] text-gray-400">{top3[2].handle}</div>
                    <div className="flex items-center justify-center gap-1 mt-1">
                      <i className={(platformIcons[top3[2].platform] || "ri-share-line") + " text-xs text-gray-500"}></i>
                      <span className="text-[10px] text-gray-500">{top3[2].platform}</span>
                    </div>
                  </div>
                  <div className="w-full bg-amber-50 border border-amber-200 rounded-t-xl h-16 flex flex-col items-center justify-center">
                    <div className="text-base font-extrabold text-amber-700">${(top3[2].commissionThisMonth / 1000).toFixed(1)}K</div>
                    <div className="text-[10px] text-gray-400">{top3[2].salesThisMonth} sales</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Full Rankings Table */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-sm font-bold text-gray-900">Full Rankings</h2>
              <span className="text-xs text-gray-400">{filtered.length} resellers</span>
            </div>
            <div className="divide-y divide-gray-50">
              {filtered.map((reseller) => {
                const tier = tierColors[reseller.tier] || tierColors.Silver;
                return (
                  <div
                    key={reseller.rank}
                    className={`flex items-center gap-4 px-6 py-4 hover:bg-gray-50 transition-colors ${reseller.isCurrentUser ? "bg-[#fdf8f0] border-l-4 border-[#D4AF37]" : ""}`}
                  >
                    {/* Rank */}
                    <div className={`w-8 h-8 flex items-center justify-center rounded-full font-extrabold text-sm flex-shrink-0 ${
                      reseller.rank === 1 ? "bg-[#D4AF37] text-white" :
                      reseller.rank === 2 ? "bg-gray-400 text-white" :
                      reseller.rank === 3 ? "bg-amber-600 text-white" :
                      "bg-gray-100 text-gray-600"
                    }`}>
                      {reseller.rank}
                    </div>

                    {/* Avatar */}
                    <div className="relative flex-shrink-0">
                      <img src={reseller.avatar} alt={reseller.name} className="w-10 h-10 rounded-full object-cover object-top" />
                      {reseller.isCurrentUser && (
                        <div className="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center bg-[#D4AF37] rounded-full">
                          <i className="ri-user-fill text-white text-[8px]"></i>
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-gray-900">{reseller.name}</span>
                        {reseller.isCurrentUser && (
                          <span className="text-[10px] font-bold bg-[#D4AF37] text-white px-1.5 py-0.5 rounded-full">You</span>
                        )}
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${tier.bg} ${tier.text} ${tier.border}`}>
                          {reseller.tier}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 text-[10px] text-gray-400 flex-wrap">
                        <span>{reseller.handle}</span>
                        <span className="flex items-center gap-1">
                          <i className={(platformIcons[reseller.platform] || "ri-share-line") + " text-xs"}></i>
                          {reseller.platform}
                        </span>
                        <span>{reseller.followers} followers</span>
                        <span className="hidden sm:inline">{reseller.country}</span>
                      </div>
                      <div className="text-[10px] text-gray-400 mt-0.5 hidden md:block">
                        Top: <span className="text-gray-600 font-medium">{reseller.topProduct}</span>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="hidden sm:flex items-center gap-6 flex-shrink-0">
                      <div className="text-center">
                        <div className="text-sm font-bold text-gray-900">{reseller.salesThisMonth}</div>
                        <div className="text-[10px] text-gray-400">Sales</div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm font-bold text-[#C41E3A]">${reseller.commissionThisMonth.toLocaleString()}</div>
                        <div className="text-[10px] text-gray-400">Commission</div>
                      </div>
                      <div className="text-center">
                        <div className={`text-sm font-bold ${reseller.growth.startsWith("+") ? "text-green-600" : "text-red-500"}`}>
                          {reseller.growth}
                        </div>
                        <div className="text-[10px] text-gray-400">Growth</div>
                      </div>
                    </div>

                    {/* Mobile commission */}
                    <div className="sm:hidden text-right flex-shrink-0">
                      <div className="text-sm font-bold text-[#C41E3A]">${reseller.commissionThisMonth.toLocaleString()}</div>
                      <div className="text-[10px] text-gray-400">{reseller.growth}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Motivation CTA */}
          <div className="bg-gradient-to-r from-[#1a0a0a] to-[#3a1010] rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <div className="text-white font-bold text-base mb-1">Want to climb the leaderboard?</div>
              <div className="text-white/60 text-xs">Browse more factory products and generate referral links to boost your sales this month.</div>
            </div>
            <a
              href="/reseller/products"
              className="flex items-center gap-2 bg-[#D4AF37] text-[#1a0a0a] px-5 py-3 rounded-xl text-sm font-bold hover:bg-[#c9a227] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-store-2-line"></i>
              Browse Products
            </a>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ResellerLeaderboardPage;
